
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { GetServerSideProps } from 'next';
import { getAssetsCdnConfig } from '@weee-fe/module';
import { getHttpReqAwareProps } from '@/utils/http-req-aware-helper';
import { getAppStatusBarHeight, isMobile, isSayWeeeApp, isIOS } from '@/utils/common/device';
import Layout from '@/layouts/default';
import StudentBenefitsHeader from '@/components/sutdent-benefits/student-benefits-header';
import { shareButtonClick } from '@/utils/weeeTracker/methods';
import StudentPartnerInfo from '@/components/sutdent-benefits/student-partner-info';
import BenefitCards from '@/components/sutdent-benefits/benefit-cards';
import BenefitsList from '@/components/sutdent-benefits/benefits-list';
import ExclusiveEvent from '@/components/sutdent-benefits/exclusive-event';
import { getCmsConfigData } from '@/api/content';
import { getCookie, setCookie } from '@/utils/cookie';
import Constants from '@/constants/const';
import StudentCmsSection, { findStaicDataFetch } from '@/components/sutdent-benefits/student-cms-section';
import { getStudentBenefitsData, getStudentBenefitsShareInfo } from '@/api/growth';
import { buildUrlWithOrigin } from '@/utils/url';
import Head from 'next/head';
import LANDING_BG from '@/components/sutdent-benefits/images/landing-bg.png';
import PreVerification from '@/components/sutdent-benefits/pre-verification';
import { isLatinoChannel } from '@/utils/common/latino-app';
import useScrollOver from '@/hooks/useScrollOver';
import { useMemo, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import usePageShow from '@/hooks/page-show';
import BENEFITS_BG from '@/components/sutdent-benefits/images/benefits-bg.png';
import { getValueFromCookie } from '@/utils/cookie';
import { callAppFunction } from '@weee-fe/utilities';
import { isAboveVersions } from '@/utils/tool';

const StudentBenefits = props => {
  const { barHeight, isApp, shareData, isPC, sections = [], datasource = {}, staticRes = {}, data } = props;
  const router = useRouter();
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  useEffect(() => {
    if (!data?.is_verified) return;

    const key = Constants.HAS_VISITED_STUDENT_BENEFITS;

    // 处理访问状态
    const handleVisitedStatus = val => {
      const hasVisited = !!val;
      if (!hasVisited) {
        // 如果是首次访问，则标记为已访问
        if (isSayWeeeApp()) {
          if (isAboveVersions(['20.6.1', '19.9'])) {
            callAppFunction('nativeStore', {
              method: 'set',
              key,
              value: 'true'
            });
          }
        } else {
          setCookie(key, 'true', null, {
            maxAge: 365 * 24 * 60 * 60 * 3
          });
        }
      }
      setIsFirstVisit(!hasVisited);
    };

    if (isSayWeeeApp()) {
      // App 环境：异步从 Native 存储获取
      callAppFunction(
        'nativeStore',
        {
          method: 'get',
          key
        },
        res => {
          const val = res?.object?.[key];
          handleVisitedStatus(val);
        }
      );
    } else {
      // Web 环境：同步从 Cookie 获取
      const val = getValueFromCookie(key);
      handleVisitedStatus(val);
    }
  }, [data?.is_verified]);

  // 从其他页面返回时（如浏览器后退、bfcache 恢复），重新请求 getServerSideProps 以拿到最新状态（如认证结果）
  usePageShow(() => {
    router.replace(router.asPath);
  }, false);

  const title = data?.navigation_bar?.content?.title;

  const trackingContext = useMemo(
    () => ({
      page_target: data?.products?.page_key
    }),
    [data?.products?.page_key]
  );

  const { isScrollOver } = useScrollOver({
    targetHeight: 0
  });

  useEffect(() => {
    if (isIOS()) {
      const handleFocusOut = () => {
        // 软键盘收起后，iOS 页面有时不会回弹，手动触发一次 scroll 强制重绘
        setTimeout(() => {
          const scrollHeight = document.documentElement.scrollTop || document.body.scrollTop || 0;
          window.scrollTo(0, Math.max(scrollHeight - 1, 0));
        }, 100);
      };

      document.body.addEventListener('focusout', handleFocusOut);
      return () => {
        document.body.removeEventListener('focusout', handleFocusOut);
      };
    }
  }, []);

  return (
    <Layout
      appStatusBarHeight={barHeight}
      statusBarColor={isScrollOver ? '#fff' : 'rgba(255, 255, 255, 0)'}
      blockStreamlinedHeader={true} // 强制使用基础Header
      style={
        data?.is_verified
          ? {
              backgroundImage: `url(${BENEFITS_BG})`,
              backgroundSize: isApp ? '100% 359px' : '100% 300px',
              backgroundPosition: data?.hero_section?.benefits?.length ? 'top center' : 'top -156px center',
              backgroundRepeat: 'no-repeat'
            }
          : {
              backgroundImage: `url(${LANDING_BG})`,
              backgroundSize: isApp ? '100% 590px' : '100% 543px',
              backgroundPosition: 'top center',
              backgroundRepeat: 'no-repeat'
            }
      }
      header={
        <StudentBenefitsHeader
          className={isScrollOver ? 'bg-surface-100-bg' : 'bg-transparent'}
          shareData={shareData}
          title={data?.is_verified ? null : title}
          shareBeforeClick={() => shareButtonClick({ co: { target_type: 'student_hub' } })}
        />
      }>
      {!!shareData?.meta_properties && (
        <Head>
          <meta name="format-detection" content="telephone=no" />
          <meta
            property="fb:app_id"
            content={`${process.env.WEEE_FACEBOOK_APPID || process.env.NEXT_PUBLIC_WEEE_FACEBOOK_APPID}`}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={shareData.meta_properties?.fb_url} />
          <meta property="og:title" content={shareData.meta_properties?.fb_title} />
          <meta property="og:description" content={shareData.meta_properties?.fb_description} />
          <meta property="og:image" content={`${shareData.meta_properties?.fb_image}`} />
          <meta property="og:image:alt" content={shareData.meta_properties?.fb_title} />
          {shareData?.meta_properties?.fb_width && (
            <meta property="og:image:width" content={shareData?.meta_properties?.fb_width} />
          )}
          {shareData?.meta_properties?.fb_height && (
            <meta property="og:image:height" content={shareData?.meta_properties?.fb_height} />
          )}
        </Head>
      )}
      <div className="pb-safe">
        <>
          {data?.is_verified ? (
            <div>
              {isFirstVisit ? (
                <>
                  <BenefitCards isPC={isPC} data={data} />
                  <BenefitsList isPC={isPC} data={data} />
                  <ExclusiveEvent data={data} />
                  <StudentCmsSection
                    sections={sections}
                    datasource={datasource}
                    staticRes={staticRes}
                    trackingContext={trackingContext}
                  />
                </>
              ) : (
                <>
                  <BenefitCards isPC={isPC} data={data} />
                  <StudentCmsSection
                    sections={sections}
                    datasource={datasource}
                    staticRes={staticRes}
                    trackingContext={trackingContext}
                  />
                  <div className="w-full h-[1px] border-b border-solid border-surface-100-hairline"></div>
                  <ExclusiveEvent data={data} className="border-b border-solid border-surface-100-hairline" />
                  <BenefitsList isPC={isPC} data={data} className="border-b-0" />
                </>
              )}
            </div>
          ) : (
            <PreVerification {...props} />
          )}
        </>
        <StudentPartnerInfo />
      </div>
    </Layout>
  );
};

export default StudentBenefits;

 const _getServerSideProps: GetServerSideProps = async ctx => {
  const { query, locale } = ctx;
  const statusBarHeight = getAppStatusBarHeight(ctx);
  const isPC = !isMobile(ctx) && !isSayWeeeApp(ctx);
  const lang = locale === Constants.LANGUAGE_CHINESE_T ? Constants.LANGUAGE_ZHT_ALIAS : locale;
  const isLatino = isLatinoChannel(ctx);

  // 如果是latino渠道，跳转首页
  if (isLatino) {
    return {
      redirect: {
        destination: buildUrlWithOrigin(`/${locale}`),
        permanent: false
      }
    };
  }

  const [studentBenefitsRes, studentBenefitsShareRes] = await Promise.all([
    getStudentBenefitsData({ ctx }),
    getStudentBenefitsShareInfo({ ctx })
  ]);

  // 获取学生权益数据失败，跳转首页
  if (!studentBenefitsRes?.result) {
    return {
      redirect: {
        destination: buildUrlWithOrigin(`/${locale}`),
        permanent: false
      }
    };
  }

  const studentData = studentBenefitsRes?.object || {};
  let sections = [];
  let datasource = {};
  let staticRes = {};

  // 已认证且有 products 时，在服务端拉取 CMS 配置与静态数据，首屏即可渲染
  if (studentData?.is_verified && studentData?.products) {
    try {
      const cmsRes = await getCmsConfigData({ ...studentData.products }, { ctx });
      if (cmsRes?.result) {
        const resData = cmsRes.object;
        sections = resData?.layout?.sections || [];
        datasource = resData?.datasource || {};
        staticRes = await findStaicDataFetch({
          sections,
          datasource,
          fetchParams: {
            lang,
            zipcode: getCookie(Constants.ZIP_CODE, ctx),
            date: getCookie(Constants.DELIVERY_DATE, ctx)
          },
          ctx
        });
      }
    } catch (error) {
      console.error('Failed to fetch CMS data on server', error);
    }
  }

  return {
    props: {
      data: studentData,
      shareData: studentBenefitsShareRes?.object || {},
      barHeight: statusBarHeight,
      isApp: isSayWeeeApp(ctx),
      isPC: isPC,
      lang,
      sections,
      datasource,
      staticRes,
      ...getAssetsCdnConfig(ctx),
      ...getHttpReqAwareProps(ctx)
    }
  };
};


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/student-benefits/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  